import qs from 'qs'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'

import { SORTER_KEYS, EXCLUSIVE_URL_BLOCKED_PARAMS } from './constants'

const stringifyQuery = (
  query,
  { addQueryPrefix = true, ...restOptions } = {}
) => {
  const options = {
    encodeValuesOnly: true,
    skipNulls: true,
    ...restOptions,
    addQueryPrefix: false
  }

  const parsedQuery = cloneDeep(query)
  const paramsKeys = Object.keys(parsedQuery)

  const queryArray = paramsKeys
    .filter(key => !!parsedQuery[key].value)
    .map(key => {
      const param = parsedQuery[key]

      const filterItem =
        param.type === 'filter'
          ? { filter: { [key]: param.value } }
          : { [key]: param.value }

      return qs.stringify(filterItem, options)
    })

  const queryString = queryArray.join('&')

  return queryString !== '' && addQueryPrefix ? `?${queryString}` : queryString
}

const objectifyQuery = queryString => {
  if (!queryString) return {}

  const getType = name => (SORTER_KEYS.includes(name) ? 'sorter' : 'other')

  const queryArray = queryString.replace('?', '').split('&')

  const newQuery = queryArray.reduce((queryObject, param) => {
    const parsedParam = qs.parse(param)
    const isFilter = Boolean(parsedParam.filter)
    const parsedObject = isFilter ? parsedParam.filter : parsedParam

    const [name, value] = Object.entries(parsedObject)[0]

    return {
      ...queryObject,
      [name]: {
        type: isFilter ? 'filter' : getType(name),
        value
      }
    }
  }, {})

  return newQuery
}

const replacer = (str, oldSeparator, newSeparator) => {
  return str.replace(new RegExp(oldSeparator, 'g'), newSeparator)
}

const normalizePath = path => {
  if (!path) {
    return null
  }

  return path.charAt(0) === '/' ? path : `/${path}`
}

const exclusiveUrlToParams = (pathName, basePath) => {
  const exlusiveUrlArray = pathName
    .split('/')
    .filter(item => item && item !== basePath)

  if (exlusiveUrlArray.length < 1) {
    return {}
  }

  const key = exlusiveUrlArray[0]
  const value = exlusiveUrlArray.slice(1).join('/')

  if (value) {
    return {
      [key]: {
        value,
        type: 'filter'
      }
    }
  }

  switch (key) {
    case 'ofertas':
      return {
        produtos: {
          value: 'ofertas',
          type: 'sorter'
        }
      }

    case 'melhor-preco':
    case 'menor-preco':
      return {
        order: {
          value: key,
          type: 'sorter'
        }
      }

    default:
      return {
        term: {
          value: replacer(key, '-', ' '),
          type: 'other'
        }
      }
  }
}

const paramsToExclusiveUrl = query => {
  const clonedQuery = cloneDeep(query)

  if (isEmpty(clonedQuery)) {
    return {
      exclusiveUrl: '',
      restQuery: {}
    }
  }

  const exclusiveKey = Object.keys(clonedQuery).find(
    key =>
      !EXCLUSIVE_URL_BLOCKED_PARAMS.includes(key) &&
      clonedQuery[key].type !== 'other'
  )

  if (!exclusiveKey) {
    return {
      exclusiveUrl: '',
      restQuery: clonedQuery
    }
  }

  const { value, type } = clonedQuery[exclusiveKey]

  delete clonedQuery[exclusiveKey]

  const exclusiveUrl =
    type === 'filter'
      ? normalizePath(`${exclusiveKey}/${value}`)
      : normalizePath(value)

  return {
    exclusiveUrl,
    restQuery: clonedQuery
  }
}

/**
 *
 * @param {object} { location } react router prop
 * @param {string} { basePath } base path name from EntryKey prop
 * @param {boolean} { isMappedSearch }
 */
const urlToObject = ({ location, url: basePath }) => {
  const exclusiveUrlParams = exclusiveUrlToParams(location.pathname, basePath)

  const parsedQuery = objectifyQuery(location.search)

  if (isEmpty(exclusiveUrlParams) && isEmpty(parsedQuery)) {
    return {}
  }

  return {
    ...exclusiveUrlParams,
    ...parsedQuery
  }
}

/**
 *
 * @param {string} basepath
 * @param {object} query { order: string, filter: object, page: int }
 */
const buildPath = (basepath, query = {}, isMappedSearch) => {
  const pathname = normalizePath(basepath)
  const clonedQuery = cloneDeep(query)

  if (isEmpty(clonedQuery)) {
    return { pathname }
  }

  if (isMappedSearch) {
    const termValue = clonedQuery.term.value
    delete clonedQuery.term

    return {
      pathname: replacer(termValue, ' ', '-'),
      search: stringifyQuery(clonedQuery)
    }
  }

  if (clonedQuery.term) {
    return {
      search: stringifyQuery(clonedQuery)
    }
  }

  if (!isEmpty(clonedQuery)) {
    const { exclusiveUrl, restQuery } = paramsToExclusiveUrl(clonedQuery)

    return {
      pathname: pathname + exclusiveUrl,
      search: stringifyQuery(restQuery)
    }
  }
}

const removeFilterQueries = query => {
  const queryArray = Object.entries(query)

  const newQuery = queryArray.reduce((object, item) => {
    const key = item[0]
    const value = item[1]

    const shouldBeRemoved =
      key === 'page' || value.type === 'filter' || value.type === 'sorter'

    if (!shouldBeRemoved) {
      object[key] = value
    }

    return object
  }, {})

  return newQuery
}

const buildPagination = ({
  query,
  currentPage,
  pageCount,
  baseUrl,
  isMappedSearch
}) => {
  if (!currentPage || !pageCount) return {}
  const hasNextPage = currentPage < pageCount
  const hasPrevPage = currentPage >= 2
  const result = {}
  const buildPaginationURL = pageQuery => {
    const { pathname = window.location.pathname, search = '' } = buildPath(
      baseUrl,
      { ...query, ...pageQuery },
      isMappedSearch
    )
    return `${origin}${normalizePath(pathname)}${search}`
  }
  if (hasNextPage) {
    result.nextPage = buildPaginationURL({
      page: { value: currentPage + 1, type: 'other' }
    })
  }
  if (hasPrevPage) {
    result.prevPage = buildPaginationURL({
      page: { value: currentPage - 1, type: 'other' }
    })
  }
  return result
}

export {
  stringifyQuery,
  objectifyQuery,
  exclusiveUrlToParams,
  paramsToExclusiveUrl,
  buildPath,
  urlToObject,
  replacer,
  removeFilterQueries,
  buildPagination
}
